import React from 'react'
import clsx from 'clsx'
import { Trash } from 'iconsax-react'
import { useTheme } from '@mui/material'

import BaseModal from '../../../components/molecules/ChatModals/BaseModal'
import TextRow from '../../../components/molecules/ChatModals/BaseModal/TextRow'
import Button from '../../../components/atoms/Button2'
import ButtonSecondary from '@SonarCX/components/atoms/ButtonSecondary'
import ErrorToast from '../../../components/molecules/ErrorToast'
import StatusEdit from './StatusEdit'
import Banner from '../../../components/atoms/Banner'
import Checkbox from '../Controls/Checkbox'
import Tooltip from '../../../components/atoms/Tooltip'
import PriorityEdit from './PriorityEdit'
import Spacer from '@SonarCX/components/atoms/Spacer'

import { toTitleCase } from '../../../utilities'
import { useSettingsEdit } from '../../../providers/SettingsEditProvider'
import { useAreYouSure } from '../../../providers/AreYouSureProvider'
import useStyles from './styles'

const EditingUserType = ({
  locked,
  external,
  handleCheckboxChange,
  classes
}) => {
  if (locked) {
    return (
      <Tooltip
        title={`You can only check the name on a system user type`}
        placement='bottom-start'
        arrow
      >
        <div className={classes.disabledEditUserTypeCheckbox}>
          <Checkbox
            disabled={locked}
            checked={external}
            onCheck={handleCheckboxChange}
            className={classes.editUserTypeCheckbox}
          >
            External
          </Checkbox>
        </div>
      </Tooltip>
    )
  }
  return (
    <Checkbox
      disabled={locked}
      checked={external}
      onCheck={handleCheckboxChange}
      className={classes.editUserTypeCheckbox}
    >
      External
    </Checkbox>
  )
}

const SettingsEditModal = ({
  open,
  onClose,
  obj,
  objType,
  superType,
  handleEdit,
  handleDelete,
  locked
}) => {
  const theme = useTheme()
  const { setAction, setReason, setSubtext, setTitle, setV2 } = useAreYouSure()
  const { save } = useSettingsEdit()

  const [name, setName] = React.useState(obj.name)
  const [external, setExternal] = React.useState(obj?.external)
  const [error, setError] = React.useState(null)

  const isEditingUserType = objType === 'type' && superType === 'user'
  const editingUserTypeLocked = locked && isEditingUserType ? false : locked
  const classes = useStyles()

  const changed = React.useMemo(() => {
    if (isEditingUserType)
      return obj.name !== name || obj?.external !== external
    else return obj.name !== name
  }, [obj, name, external, isEditingUserType])

  const showError = React.useCallback(message => {
    setError({ err: { message } })
  }, [])

  const handleCheckboxChange = React.useCallback(() => {
    setExternal(!external)
  }, [external])

  const handleSubmit = React.useCallback(() => {
    if (!name.length) {
      showError('Name cannot be empty')
      return
    }

    try {
      if (isEditingUserType) {
        handleEdit(obj, name, external)
      } else if (objType !== 'status' && objType !== 'priority') {
        handleEdit(obj, name)
      } else {
        handleEdit(save)
      }
      onClose()
    } catch (err) {
      setError({ err })
    }
  }, [
    name,
    showError,
    handleEdit,
    onClose,
    save,
    obj,
    objType,
    external,
    isEditingUserType
  ])
  return (
    <>
      <BaseModal
        open={open}
        onClose={() => {
          if (editingUserTypeLocked) {
            onClose()
            return
          }

          const action = onClose

          if (changed) {
            setAction(action)
            setReason('You have unsaved changes')
            setV2(true)
          } else {
            action()
          }
        }}
        PaperProps={{ style: { top: '53%', width: '50%' } }}
        title={`Edit ${toTitleCase(superType)} ${toTitleCase(objType)}`}
      >
        {isEditingUserType && locked ? null : locked ? (
          <Banner className={classes.banner}>
            This is a system {objType}, it cannot be edited
          </Banner>
        ) : null}

        <div
          className={clsx(
            classes.root,
            (objType === 'status' || objType === 'priority') && classes.border
          )}
        >
          {objType === 'status' ? (
            <StatusEdit obj={obj} superType={superType} locked={locked} />
          ) : objType === 'priority' ? (
            <PriorityEdit obj={obj} superType={superType} />
          ) : (
            <div className={isEditingUserType && classes.editUserTypeContainer}>
              <TextRow
                title={`${toTitleCase(objType)} Name`}
                className={classes.row}
              >
                <input
                  value={name}
                  onChange={e => setName(e.target.value)}
                  className={classes.input}
                  maxLength={objType === 'category' ? 50 : 25}
                  disabled={editingUserTypeLocked}
                />
              </TextRow>
              {isEditingUserType && (
                <EditingUserType
                  locked={locked}
                  external={external}
                  handleCheckboxChange={handleCheckboxChange}
                  classes={classes}
                />
              )}
            </div>
          )}
        </div>

        <div className={classes.controls}>
          {!editingUserTypeLocked ? (
            <div className={classes.delete}>
              {handleDelete && (
                <Trash
                  size={theme.size.base}
                  onClick={() => {
                    const action = () => {
                      handleDelete(obj)
                      onClose()
                    }

                    setTitle('ARE YOU SURE?')
                    setAction(action)
                    setReason(
                      `Are you sure you want to delete the ${objType}: ${obj.name}?`
                    )
                    setSubtext('')
                    setV2(true)
                  }}
                />
              )}
            </div>
          ) : (
            <Spacer />
          )}

          <div className={classes.normalControls}>
            <ButtonSecondary onClick={onClose} className={classes.button}>
              CANCEL
            </ButtonSecondary>

            <Button onClick={handleSubmit} className={classes.button}>
              SAVE
            </Button>
          </div>
        </div>
      </BaseModal>

      <ErrorToast error={error} />
    </>
  )
}

export default SettingsEditModal
